import { safeAbsurd } from '../types/safeAbsurd';
import type {
  GroupEdgeSettings,
  GroupEdgeSettingsId,
  PathLookupEdgeSettings,
  PathLookupEdgeSettingsId,
  TopologyEdgeSettings,
  TopologyEdgeSettingsId,
} from './EdgeSettings';
import {
  type GraphEdgesSettings,
  isGroupEdgeSettings,
  isPathLookupEdgeSettings,
  isTopologyEdgeSettings,
} from './GraphPreset';

export interface EdgeSettingsById {
  groups: Record<GroupEdgeSettingsId, GroupEdgeSettings>;
  pathLookup: Record<PathLookupEdgeSettingsId, PathLookupEdgeSettings>;
  topology: Record<TopologyEdgeSettingsId, TopologyEdgeSettings>;
}

const reduceSettingsById = (
  acc: EdgeSettingsById,
  settings: GraphEdgesSettings,
): EdgeSettingsById =>
  settings.reduce<EdgeSettingsById>((result, setting) => {
    if (isGroupEdgeSettings(setting)) {
      result.groups[setting.id] = setting;
      reduceSettingsById(acc, setting.children);
    } else if (isTopologyEdgeSettings(setting)) {
      result.topology[setting.id] = setting;
    } else if (isPathLookupEdgeSettings(setting)) {
      result.pathLookup[setting.id] = setting;
    } else {
      safeAbsurd(setting, 'Unknown edge type');
    }
    return result;
  }, acc);

/**
 * Organize edge settings from graph settings/preset by ID
 *
 * Note: the implementation preserves object identity, therefore it can be used
 * e.g. with immer updates:
 * ```
 * produce(FactoryPreset, draft => {
 *   const edgeSettingsById = getEdgeSettingsById(draft);
 *   edgeSettingsById.groups[...].visible = false; // still immer draft, so it preserves immutability
 * });
 * ```
 */
export const getEdgeSettingsById = (
  edgesSettings: GraphEdgesSettings,
): EdgeSettingsById =>
  reduceSettingsById(
    {
      groups: {},
      pathLookup: {},
      topology: {},
    },
    edgesSettings,
  );
